// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuInfo } from 'rc-menu/lib/interface';

import { CombinedState, JobStage } from 'reducers';
import AnnotationMenuComponent, { Actions } from 'components/annotation-page/top-bar/annotation-menu';
import { updateJobAsync } from 'actions/tasks-actions';
import {
    saveAnnotationsAsync,
    setForceExitAnnotationFlag as setForceExitAnnotationFlagAction,
    removeAnnotationsAsync as removeAnnotationsAsyncAction,
    modalUpdateAsync,
    setTemporaryPhaseFlagAction
} from 'actions/annotation-actions';
import { exportActions } from 'actions/export-actions';
import { importActions } from 'actions/import-actions';
import { getCore } from 'cvat-core-wrapper';
import notification from 'antd/lib/notification';
import Modal from 'antd/lib/modal';
import Checkbox from 'antd/lib/checkbox';
import { toDisplay_V2 } from 'components/annotation-page/phase2-annotation-conf';
import { toDisplay_V1 } from 'components/annotation-page/phase2-annotation-conf_v1';
import { getTimelineFrameRate } from 'utils/media-time';
import { isTranscriptReviewComplete } from 'components/annotation-page/transcript-review-workspace/completion';

const core = getCore();
const NUM_POPUPS = 3;

interface StateToProps {
    jobInstance: any;
    stopFrame: number;
    objectStates: any[];
    isOrganizationOwner: boolean;
    jobAttributes: any,
    jobAttributeSpec: any,
    transcriptData: any;
    startFrame: number;
    dataFrameRate: number | null;
}

interface DispatchToProps {
    showExportModal: (jobInstance: any) => void;
    showImportModal: (jobInstance: any) => void;
    removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly: boolean): void;
    setForceExitAnnotationFlag(forceExit: boolean): void;
    saveAnnotations(jobInstance: any, afterSave?: () => void): void;
    updateJob(jobInstance: any): void;
    showBeforeSubmitModal(phase: string, people: any[]): void;
    setTemporaryPhaseFlag(tempPhase: boolean): void;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: {
                instance: jobInstance,
                instance: { stopFrame, startFrame, dataFrameRate },
                attributes: {
                    self: jobAttributeSpec
                }
            },
            annotations: {
                allStates,
                jobAttributes
            },
            player: {
                transcript: { data: transcriptData },
            },
        },
        auth: {
            user,
        },
        organizations: {
            current: currentOrganization,
        }
    } = state;

    return {
        jobInstance,
        stopFrame,
        objectStates: allStates,
        isOrganizationOwner: currentOrganization?.owner?.username === user?.username,
        jobAttributes,
        jobAttributeSpec,
        transcriptData,
        startFrame,
        dataFrameRate,
    };
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        showExportModal(jobInstance: any): void {
            dispatch(exportActions.openExportDatasetModal(jobInstance));
        },
        showImportModal(jobInstance: any): void {
            dispatch(importActions.openImportDatasetModal(jobInstance));
        },
        removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly: boolean) {
            dispatch(removeAnnotationsAsyncAction(startnumber, endnumber, delTrackKeyframesOnly));
        },
        setForceExitAnnotationFlag(forceExit: boolean): void {
            dispatch(setForceExitAnnotationFlagAction(forceExit));
        },
        saveAnnotations(jobInstance: any, afterSave?: () => void): void {
            dispatch(saveAnnotationsAsync(jobInstance, afterSave));
        },
        updateJob(jobInstance: any): void {
            dispatch(updateJobAsync(jobInstance));
        },
        showBeforeSubmitModal(phase: string, people: any[]): void {
            if (phase === core.enums.Phase.PHASE1A) {
                dispatch(
                    modalUpdateAsync({
                        people,
                        visible: true,
                        mode: 'duplicate_face_removal',
                    }),
                );
            } else if (phase === core.enums.Phase.PHASE1B) {
                dispatch(
                    modalUpdateAsync({
                        people,
                        visible: true,
                        mode: 'phase1b_before_submit',
                    }),
                );
            }
        },
        setTemporaryPhaseFlag(tempPhase: boolean): void {
            dispatch(setTemporaryPhaseFlagAction(tempPhase));
        },
    };
}

type Props = StateToProps & DispatchToProps & RouteComponentProps;

function AnnotationMenuContainer(props: Props): JSX.Element {
    const {
        jobInstance,
        stopFrame,
        history,
        showExportModal,
        showImportModal,
        removeAnnotations,
        setForceExitAnnotationFlag,
        saveAnnotations,
        updateJob,
        isOrganizationOwner,
        objectStates,
        showBeforeSubmitModal,
        setTemporaryPhaseFlag,
        jobAttributes,
        jobAttributeSpec,
        transcriptData,
        startFrame,
        dataFrameRate,
    } = props;

    const showPullNextModal = () => {
        Modal.confirm({
            title: 'Submit Successful',
            content: 'Would you like to claim and proceed directly to your next assigned task?',
            okText: 'Yes, Claim Next',
            cancelText: 'No, Go to Dashboard',
            onOk: () => {
                const csrfToken = document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1];
                const queryParams = new URLSearchParams(window.location.search);
                const org = queryParams.get('org');
                const url = org ? `/api/jobs/claim-next?org=${org}` : '/api/jobs/claim-next';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken || '',
                    },
                })
                .then(res => res.json())
                .then(data => {
                    if (data.job_id && data.task_id) {
                        notification.success({ message: 'Successfully claimed next task.' });
                        window.location.href = `/tasks/${data.task_id}/jobs/${data.job_id}${org ? `?org=${org}` : ''}`;
                    } else {
                        notification.warning({ message: data.detail || 'No eligible tasks are available.' });
                        history.push(org ? `/jobs?org=${org}` : '/jobs');
                    }
                })
                .catch(() => {
                    notification.error({ message: 'Error claiming next job auto-assignment.' });
                    history.push(org ? `/jobs?org=${org}` : '/jobs');
                });
            },
            onCancel: () => {
                const queryParams = new URLSearchParams(window.location.search);
                const org = queryParams.get('org');
                history.push(org ? `/jobs?org=${org}` : '/jobs');
            }
        });
    };

    const toDisplay = jobInstance.phase2a_version === '2' ? toDisplay_V2 : toDisplay_V1;

    const personAppears = (person: any, phase: any) => {
        return !person.label.attributes
            .filter((attr: any) => attr.name.startsWith(`${phase}`) && attr.name.endsWith('.doesnotappear'))
            .every((attr: any) => person.attributes[attr.id] === 'true');
    };

    const personDoesNotConributeOrAppear = (state: any, phase: any) => {
        let doesNotContributeValues: any = [];
        let doesNotAppearValues: any = [];
        let doesNotAppearAttributes = state.label.attributes.filter(
            (attr: any) => attr.name.startsWith(`${phase}.popup`) && attr.name.endsWith('doesnotappear'),
        );
        doesNotAppearAttributes.forEach((attr: any) => {
            // get popup number
            let popupNumber = parseInt(attr.name.split('.')[2]);
            doesNotAppearValues[popupNumber] = state.attributes[attr.id] === '' ? 'false' : state.attributes[attr.id];
        });

        let doesNotContributeAttributes = state.label.attributes.filter((attr: any) =>
            attr.name.endsWith('doesnotcontribute'),
        );
        doesNotContributeAttributes.forEach((attr: any) => {
            // get popup number
            let popupNumber = parseInt(attr.name.split('.')[2]);
            doesNotContributeValues[popupNumber] =
                state.attributes[attr.id] === '' ? 'false' : state.attributes[attr.id];
        });
        let bitwiseOr = doesNotAppearValues.map(
            (val: any, index: number) =>
                (val === 'true' ? true : false) || (doesNotContributeValues[index] === 'true' ? true : false),
        );

        return bitwiseOr.every((val: any) => val === true);
    };

    const popupsFilled = (state: any, phase: any) => {
        if (phase === core.enums.Phase.PHASE2A) {
            return [...Array(NUM_POPUPS).keys()].every((idx) => {
                const doesNotAppearOrContribAttrs = state.label.attributes.filter(
                    (attr: any) =>
                        attr.name.startsWith(`${phase}.popup.${idx}`) &&
                        (attr.name.endsWith('doesnotappear') || attr.name.endsWith('doesnotcontribute')),
                );

                const doesNotAppearOrContribVals = doesNotAppearOrContribAttrs.map((attr: any) => {
                    return state.attributes[attr.id] === '' ? 'false' : state.attributes[attr.id];
                });

                // either does not appear or contribute could be checked, which is why we use some
                const doesNotAppearOrContrib = doesNotAppearOrContribVals.some((val: any) => val === 'true');

                const popupAttrs = state.label.attributes.filter(
                    (attr: any) =>
                        attr.name.startsWith(`${phase}.popup.${idx}`) &&
                        !attr.name.includes('recurring') &&
                        !attr.nullable &&
                        toDisplay(
                            state.label.attributes,
                            jobAttributeSpec,
                            state.attributes,
                            jobAttributes,
                            attr,
                            objectStates,
                        ),
                );

                const popupVals = popupAttrs.every((attr: any) => {
                    return state.attributes[attr.id] !== '';
                });

                return popupVals || doesNotAppearOrContrib;
            });
        } else if (phase === core.enums.Phase.PHASE2B) {
            const doesNotAppearAttr = state.label.attributes.filter((attr: any) => attr.name.startsWith('phase2b.popup') && attr.name.endsWith('doesnotappear'))[0];
            const doesNotAppearData = state.attributes[doesNotAppearAttr.id] != '' ? JSON.parse(state.attributes[doesNotAppearAttr.id]) : {};
            if (
                Object.keys(doesNotAppearData).length !== 0 &&
                Object.values(doesNotAppearData).every((val) => val === 'true')
            ) {
                return true;
            }
            return state.label.attributes
                .filter(
                    (attr: any) =>
                        attr.name.startsWith(`${phase}.popup`) &&
                        !attr.name.includes('recurring') &&
                        !attr.nullable &&
                        toDisplay(
                            state.label.attributes,
                            jobAttributeSpec,
                            state.attributes,
                            jobAttributes,
                            attr,
                            objectStates,
                        ),
                )
                .every((attr: any) => state.attributes[attr.id] !== '');
        } else if (phase === core.enums.Phase.PHASE3A) {
            return [...Array(NUM_POPUPS).keys()].every((idx) => {
                const doesNotAppearOrContribAttrs = state.label.attributes.filter(
                    (attr: any) =>
                        attr.name.startsWith(`${phase}.popup.${idx}`) &&
                        (attr.name.endsWith('doesnotappear') || attr.name.endsWith('doesnotcontribute')),
                );

                const doesNotAppearOrContribVals = doesNotAppearOrContribAttrs.map((attr: any) => {
                    return state.attributes[attr.id] === '' ? 'false' : state.attributes[attr.id];
                });

                // either does not appear or contribute could be checked, which is why we use some
                const doesNotAppearOrContrib = doesNotAppearOrContribVals.some((val: any) => val === 'true');

                const popupAttrs = state.label.attributes.filter(
                    (attr: any) =>
                        attr.name.startsWith(`${phase}.popup.${idx}`) &&
                        !attr.name.includes('recurring') &&
                        !attr.nullable &&
                        toDisplay(
                            state.label.attributes,
                            jobAttributeSpec,
                            state.attributes,
                            jobAttributes,
                            attr,
                            objectStates,
                        ),
                );

                const popupVals = popupAttrs.every((attr: any) => {
                    return state.attributes[attr.id] !== '';
                });

                return popupVals || doesNotAppearOrContrib;
            });
        }
    };

    const recurringPopupsFilled = (state: any) => {
        const framesToCheck: any = state.label.attributes
            .filter((attr: any) => attr.name.startsWith('phase2b.popup') && attr.name.endsWith('doesnotappear'))
            .reduce((acc: any, attr: any) => {
                const vals = JSON.parse(state.attributes[attr.id]);
                Object.keys(vals).forEach((key) => {
                    if (vals[key] === 'false') {
                        acc.push(key);
                    }
                });
                return acc;
            }, []);
        // fetch the attributes we have to check these frames for
        let recurringAttributes = state.label.attributes.filter(
            (attr: any) =>
                attr.name.includes('recurring') &&
                !attr.nullable &&
                toDisplay(
                    state.label.attributes,
                    jobAttributeSpec,
                    state.attributes,
                    jobAttributes,
                    attr,
                    objectStates,
                ),
        );
        // check if all the attributes are filled for all the frames
        const output = recurringAttributes.every((attr: any) => {
            return framesToCheck.every((frame: any) => JSON.parse(state.attributes[attr.id])[frame] !== '');
        });

        return output;
    };

    const situationLabelsFilled = (phase: any) => {
        const attributes = jobAttributeSpec.filter(
            (attr: any) =>
                attr.name.includes(phase) &&
                !attr.nullable &&
                toDisplay(jobAttributeSpec, jobAttributeSpec, jobAttributes, jobAttributes, attr, objectStates),
        );
        const output = attributes.every((attr: any) => {
            return jobAttributes[attr.id] !== '';
        });
        return output;
    };

    const sidebarQuestionsFilled = (state: any, phase: any) => {
        const attributes = state.label.attributes.filter(
            (attr: any) =>
                attr.name.startsWith(`${phase}.sidebar`) &&
                !attr.nullable &&
                toDisplay(
                    state.label.attributes,
                    jobAttributeSpec,
                    state.attributes,
                    jobAttributes,
                    attr,
                    objectStates,
                ),
        );
        const output = attributes.every((attr: any) => {
            return state.attributes[attr.id] !== '';
        });
        return output;
    };

    const onClickMenu = async (params: MenuInfo): Promise<void> => {
        const [action] = params.keyPath;
        if (action === Actions.EXPORT_JOB_DATASET) {
            showExportModal(jobInstance);
        } else if (action === Actions.RENEW_JOB) {
            jobInstance.state = core.enums.JobState.NEW;
            jobInstance.stage = JobStage.ANNOTATION;
            updateJob(jobInstance);
            window.location.reload();
        } else if (action === Actions.FINISH_JOB) {
            if (jobInstance.phase === core.enums.Phase.PHASE0) {
                // Move to phase1a and set state to new
                jobInstance.phase = core.enums.Phase.PHASE1A;
                jobInstance.state = core.enums.JobState.NEW;
                updateJob(jobInstance);
                setTimeout(() => {
                    showPullNextModal();
                }, 1000);
            } else if (jobInstance.phase === core.enums.Phase.PHASE1A) {
                const people = objectStates
                    .filter((state) => {
                        if (state.label.name.startsWith('person:')) {
                            return true;
                        }
                        return false;
                    })
                    .map((person) => ({
                        clientID: person.clientID,
                        frameNumber: person.frame,
                    }));
                showBeforeSubmitModal(core.enums.Phase.PHASE1A, people);
            } else if (jobInstance.phase === core.enums.Phase.PHASE1B) {
                const people = objectStates
                    .filter((state) => {
                        if (state.label.name.startsWith('person:')) {
                            return true;
                        }
                        return false;
                    })
                    .map((person) => ({
                        clientID: person.clientID,
                        frameNumber: person.frame,
                    }));
                showBeforeSubmitModal(core.enums.Phase.PHASE1B, people);
            } else if (jobInstance.phase === core.enums.Phase.PHASE2A) {
                const situationLabels = situationLabelsFilled(core.enums.Phase.PHASE2A);
                const people = objectStates.filter((state: any) => state.label.name.startsWith('person:'));
                const canSubmit =
                    people.every((person: any) => {
                        const appears = personAppears(person, core.enums.Phase.PHASE2A);
                        const popupsAreFilled = popupsFilled(person, core.enums.Phase.PHASE2A);
                        const doesNotContributeOrAppear = personDoesNotConributeOrAppear(person, core.enums.Phase.PHASE2A);
                        const sidebarFilled = sidebarQuestionsFilled(person, core.enums.Phase.PHASE2A);
                        // If the person appears then all the popups should be filled
                        // If the person appears and the bitwise or of does not contribute and does not appear is true
                        // then the popups need not be filled
                        return (appears && popupsAreFilled && sidebarFilled) || (doesNotContributeOrAppear && appears);
                    }) && situationLabels;
                if (!canSubmit) {
                    notification.error({
                        message: `Cannot submit when questions are unanswered`,
                    });
                    return;
                }

                jobInstance.state = core.enums.JobState.NEW;
                jobInstance.phase = core.enums.Phase.PHASE2B;
                updateJob(jobInstance);
                setTimeout(() => {
                    showPullNextModal();
                }, 1000);
            } else if (jobInstance.phase === core.enums.Phase.PHASE2B) {
                const people = objectStates.filter((state: any) => state.label.name.startsWith('person:'));
                const canSubmit =
                    people.every(
                        (person: any) =>
                            recurringPopupsFilled(person) && popupsFilled(person, core.enums.Phase.PHASE2B),
                    ) && situationLabelsFilled(core.enums.Phase.PHASE2B);
                if (!canSubmit) {
                    notification.error({
                        message: `Cannot submit when questions are unanswered`,
                    });
                    return;
                }
                jobInstance.state = core.enums.JobState.COMPLETED;
                updateJob(jobInstance);
                setTimeout(() => {
                    showPullNextModal();
                }, 1000);
            } else if (jobInstance.phase === core.enums.Phase.PHASE3A) {
                const situationLabels = situationLabelsFilled(core.enums.Phase.PHASE3A);
                const people = objectStates.filter((state: any) => state.label.name.startsWith('person:'));
                const canSubmit =
                    people.every((person: any) => {
                        const appears = personAppears(person, core.enums.Phase.PHASE3A);
                        const popupsAreFilled = popupsFilled(person, core.enums.Phase.PHASE3A);
                        const doesNotContributeOrAppear = personDoesNotConributeOrAppear(person, core.enums.Phase.PHASE3A);
                        const sidebarFilled = sidebarQuestionsFilled(person, core.enums.Phase.PHASE3A);
                        // If the person appears then all the popups should be filled
                        // If the person appears and the bitwise or of does not contribute and does not appear is true
                        // then the popups need not be filled
                        return (appears && popupsAreFilled && sidebarFilled) || (doesNotContributeOrAppear && appears);
                    }) && situationLabels;
                if (!canSubmit) {
                    notification.error({
                        message: `Cannot submit when questions are unanswered`,
                    });
                    return;
                }
                jobInstance.state = core.enums.JobState.NEW;
                jobInstance.phase = core.enums.Phase.PHASE3B;
                updateJob(jobInstance);
                setTimeout(() => {
                    showPullNextModal();
                }, 1000);
            } else if (jobInstance.phase === core.enums.Phase.PHASE3B) {
                const people = objectStates.filter((state: any) => state.label.name.startsWith('person:'));
                const situationLabels = situationLabelsFilled(core.enums.Phase.PHASE3B);
                const canSubmit =
                    people.every(
                        (person: any) =>
                            sidebarQuestionsFilled(person, core.enums.Phase.PHASE3B),
                    ) && situationLabelsFilled(core.enums.Phase.PHASE3B);
                if (!canSubmit) {
                    notification.error({
                        message: `Cannot submit when questions are unanswered`,
                    });
                    return;
                }
                jobInstance.state = core.enums.JobState.COMPLETED;
                updateJob(jobInstance);
                setTimeout(() => {
                    showPullNextModal();
                }, 1000);
            } else if (
                [core.enums.Phase.PHASE4A, core.enums.Phase.PHASE4B].includes(jobInstance.phase)
            ) {
                const people = objectStates.filter((state: any) => state.label.name.startsWith('person:'));
                const canSubmit = isTranscriptReviewComplete({
                    phase: jobInstance.phase,
                    people,
                    transcriptData,
                    startFrame,
                    stopFrame,
                    frameRate: getTimelineFrameRate(dataFrameRate),
                    jobAttributeSpec,
                    jobAttributes,
                });
                if (!canSubmit) {
                    notification.error({
                        message: 'Cannot submit when transcript-review questions are unanswered',
                    });
                    return;
                }

                let isConfirmed = false;
                const modal = Modal.confirm({
                    title: 'Confirm Transcript Review Submission',
                    className: 'cvat-modal-content-finish-job-phase4',
                    width: 560,
                    content: (
                        <div style={{ marginTop: 12 }}>
                            <p style={{ marginBottom: 16 }}>
                                {jobInstance.phase === core.enums.Phase.PHASE4A
                                    ? 'This action will submit your Stage 1–4 review and advance the job to Phase 4B. Please confirm before submitting:'
                                    : 'This action will complete your Stage 5–6 transcript review. Please confirm before submitting:'}
                            </p>
                            <Checkbox
                                onChange={(e) => {
                                    isConfirmed = e.target.checked;
                                    modal.update({
                                        okButtonProps: {
                                            disabled: !isConfirmed,
                                        },
                                    });
                                }}
                            >
                                By checking this box, I confirm that this is the first time I am reading
                                these transcripts / this conversation.
                            </Checkbox>
                        </div>
                    ),
                    okButtonProps: {
                        disabled: true,
                    },
                    okText: 'Submit and continue',
                    cancelText: 'Cancel',
                    onOk: async () => {
                        if (!isConfirmed) return;

                        try {
                            await jobInstance.annotations.save();
                            await jobInstance.attributes.save(jobAttributes);
                        } catch (error) {
                            notification.error({
                                message: 'Could not save the transcript-review answers',
                                description: error instanceof Error ? error.message : `${error}`,
                            });
                            return;
                        }

                        const existingFlags = new Set(jobInstance.flags || []);
                        existingFlags.add(core.enums.JobFlag.FTR);
                        jobInstance.flags = Array.from(existingFlags);

                        if (jobInstance.phase === core.enums.Phase.PHASE4A) {
                            jobInstance.phase = core.enums.Phase.PHASE4B;
                            jobInstance.state = core.enums.JobState.NEW;
                        } else {
                            jobInstance.state = core.enums.JobState.COMPLETED;
                        }
                        updateJob(jobInstance);
                        setTimeout(() => {
                            history.push('/jobs');
                        }, 1000);
                    },
                });
            }
        } else if (action === Actions.FLAG_PROBLEM) {
            const reason = window.prompt("Please enter the reason for flagging this video as problematic:");
            if (reason === null) return; // Cancelled
            const csrfToken = document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1];
            fetch(`/api/jobs/${jobInstance.id}/flag-problematic`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                },
                body: JSON.stringify({ reason: reason || 'Unspecified problematic issue' }),
            })
            .then(response => {
                if (response.ok) {
                    notification.success({ message: 'Job flagged as problematic.' });
                    showPullNextModal();
                } else {
                    notification.error({ message: 'Failed to flag job as problematic.' });
                    history.push('/jobs');
                }
            })
            .catch(() => {
                notification.error({ message: 'Failed to flag job as problematic.' });
                history.push('/jobs');
            });

            // history.push(`/tasks/${jobInstance.taskId}`);
        } else if (action === Actions.EDIT_PHASE0) {
            setTemporaryPhaseFlag(true);
        } else if (action === Actions.BACK_TO_PHASE1A) {
            setTemporaryPhaseFlag(false);
        } else if (action === Actions.PRIMARYOFFICER_YES || action === Actions.PRIMARYOFFICER_NO) {
            if (action === Actions.PRIMARYOFFICER_YES) {
                jobInstance.phase = core.enums.Phase.PHASE0;
                updateJob(jobInstance);
                window.location.reload();
            } else {
                jobInstance.flags = jobInstance.flags.concat(core.enums.JobFlag.NPO);
                updateJob(jobInstance);
                window.location.reload();
            }
        } else if (action === Actions.OPEN_TASK) {
            history.push(`/tasks/${jobInstance.taskId}`);
        } else if (action.startsWith('state:')) {
            [, jobInstance.state] = action.split(':');
            updateJob(jobInstance);
            window.location.reload();
        } else if (action === Actions.LOAD_JOB_ANNO) {
            showImportModal(jobInstance);
        }
    };

    return (
        <AnnotationMenuComponent
            taskMode={jobInstance.mode}
            onClickMenu={onClickMenu}
            removeAnnotations={removeAnnotations}
            setForceExitAnnotationFlag={setForceExitAnnotationFlag}
            saveAnnotations={saveAnnotations}
            jobInstance={jobInstance}
            stopFrame={stopFrame}
            isOrganizationOwner={isOrganizationOwner}
        />
    );
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AnnotationMenuContainer));
